import type { EntityId } from '../sim/entity';
import type { World } from '../sim/world';
import { isDuelTargetCandidate, chebyshev } from '../duel';
import { GIANT_RAT_BODY, isStormBody, isMonsterBody } from '../sim/content';
import { isNpcName } from '../sim/npcRoster';

/**
 * Best-effort "is this a plausible player-vs-player match target?" filter, shared by the
 * renderer's blue-hover gate and the long-press menu rows (Player duels / Dice Wager):
 * not self, not a rat, within challenge range of the challenger's live tile. The server
 * rules authoritatively (player/NPC/ghost/busy across both match systems); a pick this
 * lets through that the server refuses simply fizzles.
 */
export function isPlausibleMatchTarget(
  world: World | undefined,
  playerId: EntityId | undefined,
  id: EntityId,
): boolean {
  if (playerId === undefined || id === playerId || !world) return false;
  const self = world.get(playerId)?.position;
  const target = world.get(id);
  const other = target?.position;
  if (!self || !other) return false;
  // A player actively locked in a card duel wears the replicated 'dueling'
  // interaction role (PRD #493 Slice 5, ADR-0286). The role flips live, so read
  // it here off the current world: a busy duelist is not a plausible opponent,
  // which suppresses the blue hover/click and the menu rows until the role
  // reverts to 'duel-target' at the match's end (the server also refuses busy).
  if (target?.interaction?.role === 'dueling') return false;
  const isRat = target?.renderable?.body === GIANT_RAT_BODY;
  return isDuelTargetCandidate({
    isSelf: false,
    isRat,
    distance: chebyshev(self.x, self.y, other.x, other.y),
  });
}

/**
 * The long-press menu's PvP-row gate, shared by the dice installer's "Roll the dice"
 * and the duel installer's "Duel <name>" rows so the two rows can never disagree
 * about who is an opponent. The match options show only on a real player: a
 * plausible in-range target that is not a resident NPC and not an event/scenery body
 * (a storm vortex, a chaos llama, an arena horde enemy). NPCs render identically to
 * players, so the client tells them apart by roster name (`isNpcName`, best-effort -
 * the server rules authoritatively on every challenge). A King's Justice thief/guard
 * (ADR-0194) wears a player body with a GENERATED name, so neither the roster-name
 * nor the body check can catch it - gate off the replicated `justice` marker instead
 * (the isStormBody lesson).
 */
export function isPvpMenuTarget(
  world: World | undefined,
  playerId: EntityId | undefined,
  id: EntityId,
  name: string,
): boolean {
  const entity = world?.get(id);
  return (
    isPlausibleMatchTarget(world, playerId, id) &&
    !isNpcName(name) &&
    entity?.justice === undefined &&
    !isStormBody(entity?.renderable?.body) &&
    !isMonsterBody(entity?.renderable?.body)
  );
}
